/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store the Shopify→Akeneo ID map */
  static #AKENEO_MAP_KEY = 'viewedProductsAkeneoMap';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The Shopify product ID to add.
   * @param {string} [akeneoId] - The corresponding Akeneo product ID (optional).
   */
  static addProduct(productId, akeneoId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    // Persist the Shopify → Akeneo ID mapping alongside the viewed list.
    if (akeneoId) {
      const map = this.getAkeneoIdMap();
      map[productId] = akeneoId;
      // Prune entries for products no longer in the viewed list.
      const kept = new Set(viewedProducts);
      for (const key of Object.keys(map)) {
        if (!kept.has(key)) delete map[key];
      }
      localStorage.setItem(this.#AKENEO_MAP_KEY, JSON.stringify(map));
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#AKENEO_MAP_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Retrieves the Shopify product ID → Akeneo product ID map.
   * @returns {Record<string, string>} Map of Shopify product ID to Akeneo product ID.
   */
  static getAkeneoIdMap() {
    try {
      return JSON.parse(localStorage.getItem(this.#AKENEO_MAP_KEY) || '{}');
    } catch {
      return {};
    }
  }
}
